import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../design-system/primitives/alert-dialog';
import { CoachCard } from '../../design-system/concept/coach-card';
import { ActionButton } from '../../design-system/concept/action-button';
import { useToast } from '../../design-system/primitives/use-toast';
import { FeedbackDialog } from './FeedbackDialog';
import { ClientDetailsCard } from './client-details-card';
import { formatDate } from '../../lib/date-utils';
import type { ClientData, MeasureData } from '../../lib/interfaces';
import { hasFeature, noun } from '../../lib/features';

// A scan row's kilograms and percent are fitness readings; retail lists the
// date alone.
const READINGS = hasFeature('weight') || hasFeature('bodyFat');

/**
 * Client settings: the scans, and the two destructive actions.
 *
 * Replaces EditMode, which lived behind an edit pencil on a card that no
 * longer exists and rendered as a red-bordered panel headed "Edit Mode" —
 * a mode, not a place. This is a place.
 *
 * Destructive actions stay visually quiet until they are opened. A permanent
 * full-width red slab labelled "Delete Entire Client" is the loudest thing on
 * a settings screen; the confirmation dialog, which is where the irreversible
 * step actually happens, is where the warning belongs.
 */
export const SettingsSection: React.FC<{
  gender: 'male' | 'female';
  clientName: string;
  client: ClientData;
  onSaveDetails: (patch: Partial<ClientData>) => Promise<unknown>;
  measurements: MeasureData[];
  onConfirmMeasurement: (m: MeasureData) => void;
  onDeleteMeasurement: (ids: string[]) => void;
  onDeleteClient: () => void;
}> = ({
  gender,
  clientName,
  client,
  onSaveDetails,
  measurements,
  onConfirmMeasurement,
  onDeleteMeasurement,
  onDeleteClient,
}) => {
  const { toast } = useToast();
  const [confirmation, setConfirmation] = useState('');
  const canDelete = confirmation.toUpperCase() === 'DELETE';

  const chronological = [...measurements].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const deleteScan = (id: string, date: string) => {
    onDeleteMeasurement([id]);
    toast({
      title: 'Scan deleted',
      description: `The scan from ${date} has been removed from the time series.`,
    });
  };

  return (
    <div className="flex flex-col gap-3.5">
      <ClientDetailsCard client={client} onSave={onSaveDetails} />

      <CoachCard
        testId="scan-admin"
        title={`Scans · ${measurements.length}`}
        provenance="Deleting a scan removes it from every chart and from the change-since-start figures."
      >
        {chronological.length === 0 ? (
          <p className="text-sm text-muted-foreground">No scans yet.</p>
        ) : (
          <div className="flex flex-col">
            {chronological.map((m, i) => {
              const date = formatDate(new Date(m.createdAt)) || 'Unknown date';
              return (
                <div
                  key={m.ID}
                  className={`flex items-center justify-between gap-3 py-2.5 ${
                    i < chronological.length - 1 ? 'border-b border-divider' : ''
                  }`}
                >
                  <div className="min-w-0">
                    <div className="text-xs font-medium">{date}</div>
                    {READINGS && (
                      <div className="tabular text-[10px] text-muted-foreground">
                        {m.measurements.weight
                          ? `${m.measurements.weight.toFixed(1)} kg`
                          : 'no weight'}
                        {m.measurements.body_fat
                          ? ` · ${m.measurements.body_fat.toFixed(1)} %`
                          : ''}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <FeedbackDialog
                      gender={gender}
                      measureData={m}
                      onUpdateMeasurement={onConfirmMeasurement}
                    />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          type="button"
                          aria-label={`Delete the scan from ${date}`}
                          className="rounded-md px-2 py-1.5 text-[11px] font-medium text-muted-foreground hover:bg-shell hover:text-destructive"
                        >
                          Delete
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this scan?</AlertDialogTitle>
                          <AlertDialogDescription>
                            The scan from {date} will be removed from {clientName}'s
                            time series. Every chart and the change-since-start
                            figures will be recomputed without it.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteScan(m.ID, date)}
                            className="bg-destructive hover:bg-destructive/90"
                          >
                            Delete scan
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CoachCard>

      <CoachCard testId="danger-zone" title={`Delete ${noun.client}`}>
        <p className="text-[12.5px] leading-relaxed text-foreground-soft">
          Removes {clientName} and every scan, avatar and note belonging to them.
          This cannot be undone.
        </p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              className="self-start rounded-lg border border-destructive/40 px-3.5 py-2.5 text-xs font-medium leading-none text-destructive transition-colors hover:bg-destructive hover:text-destructive-foreground"
            >
              Delete {clientName}
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {clientName}?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes the {noun.client} and all {measurements.length}{' '}
                scans, including avatars and coach notes. It cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-2">
              <label
                htmlFor="delete-confirmation"
                className="text-xs text-foreground-soft"
              >
                Type DELETE to confirm
              </label>
              <input
                id="delete-confirmation"
                type="text"
                autoComplete="off"
                placeholder="DELETE"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmation('')}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  onDeleteClient();
                  setConfirmation('');
                }}
                disabled={!canDelete}
                className="bg-destructive hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Delete {noun.client}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CoachCard>
    </div>
  );
};
